import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Info } from 'lucide-react';

import Card from '@/components/common/Card';
import ColorButton from '@/components/common/elements/ColorButton';
import devicesIcon from '@/components/common/icons/devices.svg';
import cogIcon from '@/components/common/icons/cog.svg';
import Spinner from '@/components/common/Spinner';
import classes from '@/components/InspectorsPage/InspectorFiltersDetails/style.module.scss';
import { useInspectorAppSettings } from '@/hooks/useInspectorAppSettings';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const InfoIconTooltip = ({ children, maxW = 'max-w-md' }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex items-center cursor-pointer">
        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
      </span>
    </TooltipTrigger>
    <TooltipContent
      side="top"
      className={`${maxW} text-left p-3.5 leading-relaxed bg-gray-900 text-white rounded-lg shadow-xl`}
    >
      {children}
    </TooltipContent>
  </Tooltip>
);

InfoIconTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  maxW: PropTypes.string,
};

InfoIconTooltip.defaultProps = {
  maxW: 'max-w-md',
};

const InspectorAppSettingsEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();

  const { appSettingsData, isLoading, updateSettings } = useInspectorAppSettings(id);

  const [syncMode, setSyncMode] = useState(1);
  const [detailsOrder, setDetailsOrder] = useState(0);
  const [copilotEnabled, setCopilotEnabled] = useState(true);
  const [copilotDependencyEnabled, setCopilotDependencyEnabled] = useState(false);

  // Populate local form state when query data is loaded
  useEffect(() => {
    if (appSettingsData) {
      const settings = appSettingsData?.settings || {};
      setSyncMode(settings.sync_mode ?? 1);
      setDetailsOrder(settings.details_order ?? 0);
      setCopilotEnabled(settings.copilot_enabled ?? true);
      setCopilotDependencyEnabled(settings.dependency_auto_copilot ?? settings.copilot_dependency_enabled ?? false);
    }
  }, [appSettingsData]);

  const handleCopilotToggle = isChecked => {
    setCopilotEnabled(isChecked);
    if (!isChecked) {
      setCopilotDependencyEnabled(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    updateSettings.mutate(
      {
        sync_mode: Number(syncMode),
        details_order: Number(detailsOrder),
        copilot_enabled: Boolean(copilotEnabled),
        dependency_auto_copilot: Boolean(copilotDependencyEnabled),
      },
      {
        onSuccess: () => {
          navigate(`/inspectors/active/${id}`);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className={classes.inspectorFiltersForm}>
        <Card className="fade-in">
          {/* Header Title with Back Arrow */}
          <h2 className={classes.cardHead}>
            <div className={classes.back}>
              <Link to={`/inspectors/active/${id}`}>
                <div className={classes.arrowLeft} />
              </Link>
            </div>
            <div className={classes.icon}>
              <img src={devicesIcon} alt={intl.formatMessage({ id: 'APP_SETTINGS' })} />
            </div>
            {intl.formatMessage({ id: 'APP_SETTINGS' })}
          </h2>

          {/* Subheading Configurações */}
          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <img src={cogIcon} alt={intl.formatMessage({ id: 'APP_SETTINGS_CONFIG' })} />
            </div>
            {intl.formatMessage({ id: 'APP_SETTINGS_CONFIG' })}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Grid 2 Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Sync Files Select */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                    {intl.formatMessage({ id: 'SYNC_FILES' })}
                  </label>
                  <InfoIconTooltip>
                    <div className="space-y-2 text-xs">
                      <p>
                        <strong className="text-gray-200">• {intl.formatMessage({ id: 'SYNC_FILES_ALWAYS' })}:</strong>{' '}
                        {intl.formatMessage({ id: 'SYNC_FILES_TOOLTIP_ALWAYS' })}
                      </p>
                      <p>
                        <strong className="text-gray-200">
                          • {intl.formatMessage({ id: 'SYNC_FILES_FINISHED_ONLY' })}:
                        </strong>{' '}
                        {intl.formatMessage({ id: 'SYNC_FILES_TOOLTIP_FINISHED_ONLY' })}
                      </p>
                      <p>
                        <strong className="text-gray-200">
                          • {intl.formatMessage({ id: 'SYNC_FILES_WIFI_ONLY' })}:
                        </strong>{' '}
                        {intl.formatMessage({ id: 'SYNC_FILES_TOOLTIP_WIFI_ONLY' })}
                      </p>
                    </div>
                  </InfoIconTooltip>
                </div>
                <Select value={String(syncMode)} onValueChange={val => setSyncMode(Number(val))}>
                  <SelectTrigger className="w-full bg-white border-gray-300">
                    <SelectValue placeholder={intl.formatMessage({ id: 'SELECT' })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{intl.formatMessage({ id: 'SYNC_FILES_ALWAYS' })}</SelectItem>
                    <SelectItem value="1">{intl.formatMessage({ id: 'SYNC_FILES_FINISHED_ONLY' })}</SelectItem>
                    <SelectItem value="2">{intl.formatMessage({ id: 'SYNC_FILES_WIFI_ONLY' })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Details Order Select */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                    {intl.formatMessage({ id: 'DETAILS_DISPLAY_ORDER' })}
                  </label>
                  <InfoIconTooltip>
                    <div className="space-y-2 text-xs">
                      <p>
                        <strong className="text-gray-200">
                          • {intl.formatMessage({ id: 'DETAILS_ORDER_ALPHABETIC' })}:
                        </strong>{' '}
                        {intl.formatMessage({ id: 'DETAILS_ORDER_TOOLTIP_ALPHABETIC' })}
                      </p>
                      <p>
                        <strong className="text-gray-200">
                          • {intl.formatMessage({ id: 'DETAILS_ORDER_MOST_USED' })}:
                        </strong>{' '}
                        {intl.formatMessage({ id: 'DETAILS_ORDER_TOOLTIP_MOST_USED' })}
                      </p>
                    </div>
                  </InfoIconTooltip>
                </div>
                <Select value={String(detailsOrder)} onValueChange={val => setDetailsOrder(Number(val))}>
                  <SelectTrigger className="w-full bg-white border-gray-300">
                    <SelectValue placeholder={intl.formatMessage({ id: 'SELECT' })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{intl.formatMessage({ id: 'DETAILS_ORDER_ALPHABETIC' })}</SelectItem>
                    <SelectItem value="1">{intl.formatMessage({ id: 'DETAILS_ORDER_MOST_USED' })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              {/* Copilot RV Switch */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                    {intl.formatMessage({ id: 'COPILOT_RV_ENABLED' })}
                  </label>
                  <InfoIconTooltip maxW="max-w-xs">
                    <p className="text-xs">{intl.formatMessage({ id: 'COPILOT_RV_ENABLED_TOOLTIP' })}</p>
                  </InfoIconTooltip>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={copilotEnabled} onCheckedChange={handleCopilotToggle} />
                  <span className="text-sm font-medium text-gray-700">
                    {copilotEnabled ? intl.formatMessage({ id: 'ENABLED' }) : intl.formatMessage({ id: 'DISABLED' })}
                  </span>
                </div>
              </div>

              {/* Copilot RV Dependency Switch */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                    {intl.formatMessage({ id: 'COPILOT_RV_DEPENDENCY' })}
                  </label>
                  <InfoIconTooltip maxW="max-w-xs">
                    <p className="text-xs">{intl.formatMessage({ id: 'COPILOT_RV_DEPENDENCY_TOOLTIP' })}</p>
                  </InfoIconTooltip>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={copilotDependencyEnabled}
                    disabled={!copilotEnabled}
                    onCheckedChange={checked => setCopilotDependencyEnabled(checked)}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {copilotEnabled
                      ? copilotDependencyEnabled
                        ? intl.formatMessage({ id: 'ENABLED' })
                        : intl.formatMessage({ id: 'DISABLED' })
                      : null}
                  </span>
                </div>
                {!copilotEnabled && (
                  <p className="text-xs text-gray-400 italic mt-2 font-medium">
                    *{intl.formatMessage({ id: 'COPILOT_RV_DEPENDENCY_REQUIRES' })}
                  </p>
                )}
              </div>
            </div>

            {/* Standard Footer Buttons */}
            <div className={classes.cardFooter}>
              <Link to={`/inspectors/active/${id}`} className={classes.button} disabled={updateSettings.isPending}>
                {intl.formatMessage({ id: 'CANCEL' })}
              </Link>
              <ColorButton
                type="submit"
                color="green"
                isBusy={updateSettings.isPending}
                disabled={updateSettings.isPending}
              >
                {intl.formatMessage({ id: 'SAVE' })}
              </ColorButton>
            </div>
          </form>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default InspectorAppSettingsEdit;
